import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useSettingsStore } from './settingsStore'
import { useSubscriptionStore } from './subscriptionStore'
import { useCreditsStore } from './creditsStore'
import {
  clearAuthTokens as bridgeClearAuthTokens,
  clearSubscriptionState as bridgeClearSubscriptionState,
  getPreloadApi,
  hasElectronPreload,
  startGoogleOAuth as bridgeStartGoogleOAuth,
  syncAuthTokens as bridgeSyncAuthTokens,
} from '@renderer/lib/runtime'

/**
 * Clear all account-scoped subscription/credit data so the next login
 * starts with a clean slate and doesn't inherit the previous account's token.
 */
const clearAccountSubscriptionData = (reason: string): void => {
  try {
    const hadCode = useSettingsStore.getState().activationCode !== ''
    console.warn('[SUBSCRIPTION] Clearing activation code', {
      reason,
      source: 'auth_account_clear',
      hadCode
    })
    useSettingsStore.getState().setActivationCode('')
    useSubscriptionStore.getState().clearSubscription()
    useCreditsStore.setState({ credits: 0, package: 'free', error: null })
  } catch (error) {
    console.warn('[AUTH] Failed to clear account subscription data:', error)
  }
}

const syncTokensToMainProcess = async (): Promise<void> => {
  try {
    await bridgeSyncAuthTokens()
  } catch (error) {
    console.warn('[AUTH] Failed to sync auth tokens to main process:', error)
  }
}

const clearTokensInMainProcess = async (): Promise<void> => {
  try {
    await bridgeClearAuthTokens()
  } catch (error) {
    console.warn('[AUTH] Failed to clear auth tokens in main process:', error)
  }
}

interface User {
  email: string
  name: string
  cookie?: string
}

interface AuthState {
  user: User | null
  setUser: (user: User | null) => void
  isAuthenticated: boolean
  login: (
    email: string,
    password: string
  ) => Promise<{ mfaRequired?: boolean; email?: string; tempSessionId?: string }>
  verifyMfa: (email: string, code: string, isBackupCode?: boolean) => Promise<void>
  loginWithGoogle: (mode?: 'login' | 'register') => Promise<void>
  logout: () => Promise<void>
  initializeCookie: () => Promise<boolean>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      setUser: (user: User | null): void => void set({ user, isAuthenticated: !!user }),
      login: async (
        email: string,
        password: string
      ): Promise<{ mfaRequired?: boolean; email?: string; tempSessionId?: string }> => {
        const response = await fetch(`${process.env.VITE_API_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ email, password })
        })

        if (!response.ok) {
          let errorMessage = 'Invalid credentials'
          try {
            const errorData = await response.json()
            errorMessage = errorData.error || errorData.message || errorMessage
          } catch {
            errorMessage = response.statusText || errorMessage
          }
          throw new Error(errorMessage)
        }

        const data = await response.json()

        // Check if MFA is required - return MFA info instead of throwing
        if (data.mfaRequired) {
          return {
            mfaRequired: true,
            email: data.email || email,
            tempSessionId: data.tempSessionId
          }
        }

        // Validate response structure
        if (!data || !data.user) {
          throw new Error('Invalid login response: missing user data')
        }

        if (!data.user.email) {
          throw new Error('Invalid login response: missing user email')
        }

        const token = response.headers.get('X-Auth-Token')

        // Store the token in electron mode
        if (hasElectronPreload() && token) {
          const user: User = {
            email: data.user.email,
            name: data.user.name || data.user.email,
            cookie: token
          }
          set({ user, isAuthenticated: true })
          // Add small delay to ensure Zustand persistence completes before syncing
          try {
            await new Promise((resolve) => setTimeout(resolve, 50))
            await syncTokensToMainProcess()
          } catch (error) {
            console.warn('[AUTH] Failed to sync tokens, but login succeeded:', error)
            // Don't throw - login should still succeed
          }
        } else {
          const user: User = {
            email: data.user.email,
            name: data.user.name || data.user.email
          }
          set({ user, isAuthenticated: true })
        }

        return { mfaRequired: false }
      },
      verifyMfa: async (
        email: string,
        code: string,
        isBackupCode: boolean = false
      ): Promise<void> => {
        const response = await fetch(`${process.env.VITE_API_URL}/api/auth/mfa/verify-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            email,
            code,
            isBackupCode
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Invalid verification code')
        }

        const data = await response.json()
        const token = response.headers.get('X-Auth-Token')

        // Validate response structure
        if (!data || !data.user) {
          throw new Error('Invalid MFA verification response: missing user data')
        }

        if (!data.user.email) {
          throw new Error('Invalid MFA verification response: missing user email')
        }

        // Store the token in electron mode
        if (hasElectronPreload() && token) {
          const user: User = {
            email: data.user.email,
            name: data.user.name || data.user.email,
            cookie: token
          }
          set({ user, isAuthenticated: true })
          // Add small delay to ensure Zustand persistence completes before syncing
          try {
            await new Promise((resolve) => setTimeout(resolve, 50))
            await syncTokensToMainProcess()
            await get().initializeCookie()
          } catch (error) {
            console.warn('[AUTH] Failed to sync tokens, but login succeeded:', error)
            // Don't throw - login should still succeed
          }
        } else {
          const user: User = {
            email: data.user.email,
            name: data.user.name || data.user.email
          }
          set({ user, isAuthenticated: true })
        }
      },
      loginWithGoogle: async (mode: 'login' | 'register' = 'login'): Promise<void> => {
        if (!getPreloadApi()?.startGoogleOAuth) {
          throw new Error('Google OAuth is only available in Electron app')
        }

        try {
          const result = await bridgeStartGoogleOAuth(mode)

          if (!result.success) {
            throw new Error(result.error || 'OAuth authentication failed')
          }

          if (result.token && result.user) {
            const user: User = {
              email: result.user.email,
              name: result.user.name,
              cookie: result.token
            }
            set({ user, isAuthenticated: true })
            await syncTokensToMainProcess()
          } else {
            throw new Error('OAuth succeeded but no user data received')
          }
        } catch (error) {
          throw error instanceof Error ? error : new Error('OAuth authentication failed')
        }
      },
      logout: async (): Promise<void> => {
        try {
          const response = await fetch(`${process.env.VITE_API_URL}/api/auth`, {
            method: 'DELETE',
            credentials: 'include'
          })

          if (!response.ok) {
            console.error('Failed to logout properly')
          }
        } catch (error) {
          console.error('Error during logout:', error)
        } finally {
          await clearTokensInMainProcess()
          await bridgeClearSubscriptionState()
          clearAccountSubscriptionData('user_logout')
          set({ user: null, isAuthenticated: false })
        }
      },
      initializeCookie: async (): Promise<boolean> => {
        const state = get()
        const token = state.user?.cookie

        if (hasElectronPreload() && token) {
          try {
            const response = await fetch(`${process.env.VITE_API_URL}/api/auth/token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify({ token })
            })

            if (!response.ok) {
              const isServerError = response.status >= 500
              const isAuthError = response.status === 401 || response.status === 403

              // For server errors, keep cached user (might be temporary)
              if (isServerError) {
                console.warn('[AUTH] Server error during cookie init, keeping cached user')
                return true // Don't block downstream — server might recover
              }

              // For auth errors, clear auth state so downstream code won't fire
              if (isAuthError) {
                console.error('[AUTH] Token is invalid/expired (401/403) — clearing auth state')
                await clearTokensInMainProcess()
                await bridgeClearSubscriptionState()
                clearAccountSubscriptionData('cookie_init_unauthorized')
                set({ user: null, isAuthenticated: false })
                return false
              }

              // For other errors, log but don't clear user
              console.error('Failed to initialize cookie - unknown error')
              return false
            } else {
              await syncTokensToMainProcess()
              return true
            }
          } catch (error) {
            console.error('Error initializing cookie:', error)
            // Don't clear user on network error - keep cached user for offline access
            console.log('Keeping cached user for offline access')
            return true // Network error — don't block offline usage
          }
        }
        return !!(get().user) // Return true if there's a cached user
      }
    }),
    {
      name: 'auth-storage'
    }
  )
)
